import React, { ChangeEvent, ReactElement, useState } from "react";
import { LocalizeText } from "components/localizer";
import { THeadCell } from "utils/types";
import TopBar from "components/tableComponents/topBar";
import Banner from "components/banner";
import TableComponent, { IRow } from "components/tableComponent";
import { Box, FormControlLabel, Switch, Tab, Tabs, Tooltip } from "@mui/material";
import Dropdown from "components/dropdown";
import { ModalContext } from "contexts/modals";
import UpdateContactPersonInvitation from "components/drawers/updateContactPersonInvite";
import SearchFilterField from "components/searchFilterField";
import CommunityFilterInput from "components/searchFilterComponents/communityFilter";
import { usePaginationStore } from "utils/usePaginationStore";
import StyledSwitchContainer from "components/tableComponents/switchContainer";
import { dateIsPastOrToday } from "utils/helpers";
import { getActiveStatus } from "utils/ecommerseTypes";
import { parseISO } from "date-fns";
import { IOrganization } from "reducers/memberships";

const representativeHeadCells: THeadCell[] = [
    { id: "name", sortId: "user__first_name", label: <LocalizeText tag="name" /> },
    { id: "email", sortId: "user__email", label: <LocalizeText tag="email" /> },
    { id: "communities", sortId: "membership__organization__name", label: <LocalizeText tag="organization" /> },
    { id: "customerNumber", sortId: "membership__agreement_nr", label: <LocalizeText tag="customerNumber" /> },
    { id: "community", sortId: "membership__default_community", label: <LocalizeText tag="community" /> },
    { id: "statusMembership", sortId: "status", label: <LocalizeText tag="statusMembership" /> },
    { id: "", label: "", alignCell: "right" },
];

const representativeInvitationsHeadCells: THeadCell[] = [
    { id: "name", sortId: "first_name", label: <LocalizeText tag="name" /> },
    { id: "email", sortId: "email", label: <LocalizeText tag="email" /> },
    { id: "communities", sortId: "membership__organization__name", label: <LocalizeText tag="organization" /> },
    { id: "customerNumber", sortId: "membership__agreement_nr", label: <LocalizeText tag="customerNumber" /> },
    { id: "community", sortId: "membership__default_community", label: <LocalizeText tag="community" /> },
    { id: "statusMembership", sortId: "status", label: <LocalizeText tag="statusMembership" /> },
    { id: "", label: "", alignCell: "right" },
];

type RepresentativesTab = "USERS" | "INVITATIONS";

const StaffRepresentativesView: React.FC = () => {
    const [activeFilter, setActiveFilter] = useState<boolean>(false);
    const { openDrawer } = React.useContext(ModalContext);
    const [selectedTab, setTab] = React.useState<RepresentativesTab>("USERS");
    const representativePagination = usePaginationStore("representatives", undefined, {
        page_size: 20,
        default_page_size: 20,
        is_active: true
    });
    const representativeInvitationPagination = usePaginationStore("representative-invitations", undefined, {
        page_size: 20,
        default_page_size: 20,
        is_active: true
    });

    React.useEffect(() => {
        representativePagination.getInitial();
        representativeInvitationPagination.getInitial();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getMembershipStatus = (membership: IOrganization): ReactElement => {
        if (membership.end_date) {
            return (
                <Tooltip placement="top" arrow title={<div>{membership.end_date}</div>}>
                    <div>{getActiveStatus(membership.end_date)}</div>
                </Tooltip>
            );
        } else{
            return <div>{getActiveStatus(membership.end_date)}</div>;
        }
    };

    const mapRepresentativeStaffRows = (): IRow[] =>
        representativePagination.results.map((person, index) => {
            return {
                disabled:dateIsPastOrToday(parseISO(person.membership.end_date ?? "")),
                data: [
                    person.full_name,
                    person.email,
                    person.membership.name,
                    person.membership.agreement_nr,
                    person.membership.default_community,
                    getMembershipStatus(person.membership)
                ],
            };
        });

    const mapRepresentativeInvitationsRows = (): IRow[] =>
        representativeInvitationPagination.results.map((invite, index) => {
            return {
                disabled:dateIsPastOrToday(parseISO(invite.membership.end_date ?? "")),
                data: [
                    invite.full_name,
                    invite.email,
                    invite.membership.name,
                    invite.membership.agreement_nr,
                    invite.membership.default_community,
                    getMembershipStatus(invite.membership),
                    <Dropdown
                        inRow
                        key={invite.uuid}
                        items={[
                            {
                                onClick: () =>
                                    openDrawer(UpdateContactPersonInvitation, { user: invite }, "invitations"),
                                label: <LocalizeText tag="showInvitation" />,
                            },
                        ]}
                    />,
                ],
            };
        });
    const onIncludeInactiveChange = (event: ChangeEvent<HTMLInputElement>): void => {
        representativePagination.setParams({ordering: !event.target.checked ? null : "membership__end_date", is_active: !event.target.checked});
        representativeInvitationPagination.setParams({ordering: !event.target.checked ? null : "membership__end_date", is_active: !event.target.checked});
        setActiveFilter(event.target.checked);
    };

    return (
        <div data-testid="representative-root" className="contentWrapper">
            <Banner
                title="representative"
                imageStyle={{ objectPosition: "70% 30%" }}
                image={`${process.env.PUBLIC_URL}/assets/images/arenaStaff/subscriptions.jpg`}
            />

            <TopBar
                searchField={
                    <SearchFilterField
                        pagination={
                            selectedTab === "USERS" ? representativePagination : representativeInvitationPagination
                        }
                        label={
                            selectedTab === "USERS" ? (
                                <LocalizeText tag="searchRepresentatives" />
                            ) : (
                                <LocalizeText tag="searchInvites" />
                            )
                        }
                        filterFields={
                            <CommunityFilterInput
                                pagination={
                                    selectedTab === "USERS"
                                        ? representativePagination
                                        : representativeInvitationPagination
                                }
                            />
                        }
                    />
                }
            />

            <Box>
                <Tabs value={selectedTab} onChange={(event, value) => setTab(value)}>
                    <Tab label={<LocalizeText tag="representative" />} value="USERS" />
                    <Tab label={<LocalizeText tag="invitations" />} value="INVITATIONS" />
                </Tabs>
                <StyledSwitchContainer>
                    <FormControlLabel
                        control={<Switch checked={activeFilter} onChange={onIncludeInactiveChange} color="primary" />}
                        label={<LocalizeText tag="includeTerminatedMemberships" />}
                    />
                </StyledSwitchContainer>
                {selectedTab === "USERS" && (
                    <TableComponent
                        headCells={representativeHeadCells}
                        rows={mapRepresentativeStaffRows()}
                        pagination={representativePagination}
                    />
                )}
                {selectedTab === "INVITATIONS" && (
                    <TableComponent
                        headCells={representativeInvitationsHeadCells}
                        rows={mapRepresentativeInvitationsRows()}
                        pagination={representativeInvitationPagination}
                    />
                )}
            </Box>
        </div>
    );
};

export default StaffRepresentativesView;
